import { SandboxDomainError } from '../errors';
import { SandboxBooking, SandboxState } from '../types';

export interface SandboxBookingInput {
  from: string;
  to: string;
  hosts: number[];
  client?: string | null;
  comment?: string;
  status?: SandboxBooking['status'];
}

const overlaps = (
  leftFrom: string,
  leftTo: string,
  rightFrom: string,
  rightTo: string
): boolean =>
  Date.parse(leftFrom) < Date.parse(rightTo) &&
  Date.parse(leftTo) > Date.parse(rightFrom);

const validate = (
  state: SandboxState,
  input: SandboxBookingInput,
  ignoredId?: number
): void => {
  const from = Date.parse(input.from);
  const to = Date.parse(input.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || from >= to) {
    throw new SandboxDomainError(
      'У брони должен быть корректный интервал времени'
    );
  }
  if (
    !input.hosts.length ||
    input.hosts.some((id) => !state.hosts.some((host) => host.id === id))
  ) {
    throw new SandboxDomainError('Для брони нужно выбрать существующие хосты');
  }
  const duplicate = state.bookings.find(
    (booking) =>
      booking.id !== ignoredId &&
      !['CANCELED', 'FINISHED'].includes(booking.status) &&
      booking.hostIds.some((hostId) => input.hosts.includes(hostId)) &&
      overlaps(booking.from, booking.to, input.from, input.to)
  );
  if (duplicate)
    throw new SandboxDomainError(`Хост уже занят бронью №${duplicate.id}`);
};

export const createBookingAction = (
  state: SandboxState,
  input: SandboxBookingInput
): SandboxBooking => {
  validate(state, input);
  const client = input.client
    ? state.clients.find((item) => item.uuid === input.client)
    : undefined;
  if (input.client && !client) throw new SandboxDomainError('Клиент не найден');
  const bookingId = state.nextBookingId++;
  const booking: SandboxBooking = {
    origin: 'runtime',
    id: bookingId,
    group: `runtime:booking-group:${bookingId}`,
    hostIds: [...input.hosts],
    byClient: Boolean(client),
    client: client
      ? {
          id: client.id,
          uuid: client.uuid,
          nickname: client.nickname,
          phone: client.phone,
        }
      : null,
    from: input.from,
    to: input.to,
    status: input.status ?? 'ACTIVE',
    comment: input.comment ?? '',
  };
  state.bookings.push(booking);
  return booking;
};

export const updateBookingAction = (
  state: SandboxState,
  id: number,
  input: SandboxBookingInput
): SandboxBooking => {
  const booking = state.bookings.find((item) => item.id === id);
  if (!booking) throw new SandboxDomainError('Бронь не найдена');
  if (input.status !== 'CANCELED') validate(state, input, id);
  const client = input.client
    ? state.clients.find((item) => item.uuid === input.client)
    : undefined;
  if (input.client && !client) throw new SandboxDomainError('Клиент не найден');
  Object.assign(booking, {
    hostIds: [...input.hosts],
    byClient: Boolean(client),
    client: client
      ? {
          id: client.id,
          uuid: client.uuid,
          nickname: client.nickname,
          phone: client.phone,
        }
      : null,
    from: input.from,
    to: input.to,
    status: input.status ?? booking.status,
    comment: input.comment ?? '',
  });
  return booking;
};

export const redeemBookingAction = (
  state: SandboxState,
  id: number
): SandboxBooking => {
  const booking = state.bookings.find((item) => item.id === id);
  if (!booking) throw new SandboxDomainError('Бронь не найдена');
  if (booking.status !== 'ACTIVE')
    throw new SandboxDomainError('Погасить можно только активную бронь');
  booking.status = 'REDEEMED';
  return booking;
};
